import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Users, UserPlus, Copy, Check, Mail, LogOut, Trash2, Crown, RefreshCw, Loader2, Home, X } from "lucide-react";
import { toast } from "sonner";
import { trackHouseholdCreated, trackHouseholdJoined } from "../utils/analytics";

export const HouseholdSettings = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [household, setHousehold] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [householdName, setHouseholdName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [removingMember, setRemovingMember] = useState(null);
  const [canCreate, setCanCreate] = useState(true);

  const fetchHousehold = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/household");
      setHousehold(response.data);
    } catch (error) {
      console.error("Failed to fetch household:", error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchEligibility = useCallback(async () => {
    try {
      const response = await api.get("/household/eligibility");
      setCanCreate(!!response.data.can_create);
    } catch (error) {
      setCanCreate(false);
    }
  }, [api]);

  useEffect(() => {
    fetchHousehold();
    fetchEligibility();
  }, [fetchHousehold, fetchEligibility]);

  const closeModal = () => {
    setActiveModal(null);
    setHouseholdName("");
    setInviteCode("");
    setInviteEmail("");
  };

  const handleCreateHousehold = async () => {
    if (!householdName.trim()) return;
    setActionLoading(true);
    try {
      const response = await api.post("/household/create", { name: householdName });
      setHousehold(response.data);
      closeModal();
      toast.success("Household created!");
      trackHouseholdCreated(householdName);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddSeat = async () => {
    setActionLoading(true);
    try {
      const response = await api.post("/household/add-seat/create-checkout", { origin_url: window.location.origin });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setActionLoading(false);
    }
  };


  const handleJoinHousehold = async () => {
    if (!inviteCode.trim()) return;
    setActionLoading(true);
    try {
      await api.post("/household/join", { invite_code: inviteCode.trim().toUpperCase() });
      await fetchHousehold();
      closeModal();
      toast.success("Joined household!");
      trackHouseholdJoined();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setActionLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setActionLoading(true);
    try {
      await api.post("/household/invite", { email: inviteEmail.trim() });
      closeModal();
      toast.success("Invitation sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (!household) return;
    navigator.clipboard.writeText(household.invite_code);
    setCopied(true);
    toast.success("Code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerateCode = async () => {
    setActionLoading(true);
    try {
      const response = await api.post("/household/regenerate-code");
      setHousehold(h => h ? { ...h, invite_code: response.data.invite_code } : null);
      toast.success("New code generated");
    } catch (error) {
      toast.error("Failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeave = async () => {
    setActionLoading(true);
    try {
      await api.post("/household/leave");
      setHousehold(null);
      closeModal();
      toast.success("Left household");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    setRemovingMember(memberId);
    try {
      await api.delete("/household/member/" + memberId);
      await fetchHousehold();
      toast.success("Member removed");
    } catch (error) {
      toast.error("Failed");
    } finally {
      setRemovingMember(null);
    }
  };

  if (loading) {
    return (
      <Card className="border-border/50">
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  const isOwner = household?.owner_id === user?.id;
  const members = household?.members || [];
  const seatLimit = household?.seat_limit || 5;
  const seatsFull = members.length >= seatLimit;

  return (
    <div>
      {!household ? (
        <Card className="border-border/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <CardTitle className="font-heading">Household</CardTitle>
            </div>
            <CardDescription>Share finances with family members</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!canCreate && (
              <div className="flex items-start gap-3 p-3 rounded-xl bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800" data-testid="family-required-notice">
                <Crown className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                <p className="text-sm text-violet-800 dark:text-violet-200">
                  Creating a household needs <strong>Tally Family</strong>. Upgrade above to unlock partner accounts, shared budgets and the shared vault. You can still join an existing household with an invite code.
                </p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => canCreate ? setActiveModal("create") : toast.error("Upgrade to Tally Family to create a household")}
                disabled={!canCreate}
                className="flex-1 gap-2 rounded-full"
                data-testid="create-household-btn"
              >
                {canCreate ? <Home className="h-4 w-4" /> : <Crown className="h-4 w-4" />}
                Create Household
              </Button>
              <Button variant="outline" onClick={() => setActiveModal("join")} className="flex-1 gap-2 rounded-full" data-testid="join-household-btn">
                <UserPlus className="h-4 w-4" />
                Join Household
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <CardTitle className="font-heading">{household.name}</CardTitle>
              </div>
              {isOwner && <Badge variant="secondary" className="gap-1"><Crown className="h-3 w-3" />Owner</Badge>}
            </div>
            <CardDescription>{members.length} of {seatLimit} seats used</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 rounded-xl bg-muted/50 space-y-3">
              <div className="flex items-center justify-between">
                <Label>Invite Code</Label>
                {isOwner && (
                  <Button variant="ghost" size="sm" onClick={handleRegenerateCode} disabled={actionLoading} className="h-7 text-xs">
                    {actionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" />New</>}
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-2xl font-mono tracking-[0.5em] text-center py-2 bg-background rounded-lg border">{household.invite_code}</code>
                <Button variant="outline" size="icon" onClick={handleCopyCode} data-testid="copy-invite-code">
                  {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label data-testid="seat-usage-label">Members · {members.length}/{seatLimit} seats</Label>
                <Button variant="outline" size="sm" onClick={() => seatsFull ? toast.error(`Household is full (${seatLimit} seats)`) : setActiveModal("invite")} disabled={seatsFull} className="h-7 text-xs gap-1" data-testid="invite-member-btn">
                  <Mail className="h-3 w-3" />Email Invite
                </Button>
              </div>
              {seatsFull && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 p-3" data-testid="seats-full-note">
                  <p className="text-xs text-violet-800 dark:text-violet-200 flex-1">All {seatLimit} seats are in use. {isOwner ? "Add a seat to invite one more member." : "Ask the owner to add a seat or remove a member."}</p>
                  {isOwner && (
                    <Button size="sm" onClick={handleAddSeat} disabled={actionLoading} className="rounded-full bg-violet-600 hover:bg-violet-700 text-white h-8 shrink-0" data-testid="add-seat-btn">
                      {actionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Crown className="h-3.5 w-3.5 mr-1" />Add a seat — $29/yr</>}
                    </Button>
                  )}
                </div>
              )}
              <div className="space-y-2">
                {members.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-3 rounded-lg bg-background border border-border/50">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary">{m.name?.charAt(0)?.toUpperCase() || "?"}</span>
                      </div>
                      <div>
                        <p className="font-medium text-sm flex items-center gap-2">
                          {m.name}
                          {m.role === "owner" && <Crown className="h-3 w-3 text-amber-500" />}
                          {m.id === user?.id && <Badge variant="secondary" className="text-xs">You</Badge>}
                        </p>
                        <p className="text-xs text-muted-foreground">{m.email}</p>
                      </div>
                    </div>
                    {isOwner && m.id !== user?.id && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRemoveMember(m.id)} disabled={removingMember === m.id}>
                        {removingMember === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-4 border-t">
              <Button variant="ghost" className="w-full text-destructive hover:text-destructive" onClick={() => setActiveModal("leave")} data-testid="leave-household-btn">
                <LogOut className="mr-2 h-4 w-4" />Leave Household
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {activeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeModal}>
          <div className="bg-background rounded-xl shadow-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold">
                {activeModal === "create" && "Create Household"}
                {activeModal === "join" && "Join Household"}
                {activeModal === "invite" && "Invite Member"}
                {activeModal === "leave" && "Leave Household?"}
              </h3>
              <button onClick={closeModal} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
            </div>
            
            {activeModal === "create" && (
              <div className="space-y-4">
                <div><Label>Name</Label><Input value={householdName} onChange={e => setHouseholdName(e.target.value)} placeholder="Smith Family" /></div>
                <div className="flex gap-3"><Button variant="outline" onClick={closeModal} className="flex-1">Cancel</Button><Button onClick={handleCreateHousehold} disabled={actionLoading} className="flex-1">{actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></div>
              </div>
            )}
            
            {activeModal === "join" && (
              <div className="space-y-4">
                <div><Label>Code</Label><Input value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())} placeholder="ABC123" maxLength={6} className="text-center text-2xl tracking-widest font-mono" /></div>
                <div className="flex gap-3"><Button variant="outline" onClick={closeModal} className="flex-1">Cancel</Button><Button onClick={handleJoinHousehold} disabled={actionLoading} className="flex-1">{actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join"}</Button></div>
              </div>
            )}
            
            {activeModal === "invite" && (
              <div className="space-y-4">
                <div><Label>Email</Label><Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="family@example.com" /></div>
                <div className="flex gap-3"><Button variant="outline" onClick={closeModal} className="flex-1">Cancel</Button><Button onClick={handleInvite} disabled={actionLoading} className="flex-1">{actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}</Button></div>
              </div>
            )}
            
            {activeModal === "leave" && (
              <div className="space-y-4">
                <p className="text-muted-foreground">{isOwner && members.length > 1 ? "Remove all members first." : "You will lose access to shared data."}</p>
                <div className="flex gap-3"><Button variant="outline" onClick={closeModal} className="flex-1">Cancel</Button><Button variant="destructive" onClick={handleLeave} disabled={actionLoading || (isOwner && members.length > 1)} className="flex-1">{actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Leave"}</Button></div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HouseholdSettings;
